import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";

import {
  getOrgSettings,
  isConsiderMeddpiccRoleEnabledForOrg,
  isRlAssessmentsEnabledForOrg,
} from "./rl-aggregate";
import { useAppDispatch, useAppSelector } from "../../../Store/Hooks";
import {
  fetchRlSettingConfig,
  isRlAssessmentsEnabledFromSettings,
} from "../../../Store/Reducers/VendorAdminSlice";
import {
  getFromLocalStorage,
  STORAGE_KEYS,
} from "../../../Utils/Helper/LocalStorage";
import {
  normalizeFanInCap,
  normalizeFanOutCaps,
  normalizeUnknownMeddWeight,
} from "../../../Utils/Helper/rlFanCaps";

const GradeContext = createContext(null);
const RoleContext = createContext(null);

const DEFAULT_GRADE_MAPPING = {
  grade_1: "Grade 1",
  grade_2: "Grade 2",
  grade_3: "Grade 3",
  grade_4: "Grade 4",
  grade_5: "Grade 5",
};

export function RelationshipProviders({ children }) {
  const dispatch = useAppDispatch();
  const { rlSettingValue, organizationSettings, organizationSettingsFetched } =
    useAppSelector(state => state.vendorAdmin);
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER) || {};

  useEffect(() => {
    dispatch(fetchRlSettingConfig());
  }, [dispatch]);

  const organization = useMemo(() => {
    const legacySettings = loggedInUser?.organization_settings || {};
    const rlSettings = rlSettingValue || {};
    const relationshipAssessments = organizationSettingsFetched
      ? isRlAssessmentsEnabledFromSettings(organizationSettings)
      : Boolean(
          legacySettings.relationship_assessments ??
            legacySettings.rl_assessments_enabled
        );
    const settings = {
      ...legacySettings,
      consider_meddpicc_role:
        rlSettingValue != null
          ? rlSettings.consider_meddpicc_role !== false
          : legacySettings.consider_meddpicc_role !== false,
      relationship_assessments: relationshipAssessments,
      rl_assessments_enabled: relationshipAssessments,
      fan_in_cap: normalizeFanInCap(rlSettingValue?.fan_in_cap),
      fan_out_caps: normalizeFanOutCaps(rlSettingValue?.fan_out_caps),
      unknown_meddpicc_default_weight: normalizeUnknownMeddWeight(
        rlSettingValue?.unknown_meddpicc_default_weight ??
          legacySettings.unknown_meddpicc_default_weight
      ),
    };

    return {
      id: loggedInUser?.tenantId || loggedInUser?.organization_id,
      name:
        loggedInUser?.tenantName ||
        loggedInUser?.tenant_domain ||
        "Organization",
      slug: loggedInUser?.tenantSlug || loggedInUser?.tenant_domain,
      settings,
    };
  }, [
    loggedInUser,
    rlSettingValue,
    organizationSettings,
    organizationSettingsFetched,
  ]);

  const roleValue = useMemo(
    () => ({
      organization,
      role: String(loggedInUser?.role || "").toUpperCase(),
      privilege: {
        exec_ops:
          loggedInUser?.executive_ops === true ||
          loggedInUser?.executive_ops_approve === true,
      },
      user: loggedInUser,
      rlAssessmentsEnabled: isRlAssessmentsEnabledForOrg(organization),
      considerMeddpiccRole: isConsiderMeddpiccRoleEnabledForOrg(organization),
      orgSettings: getOrgSettings(organization),
    }),
    [loggedInUser, organization]
  );

  const gradeMapping = useMemo(() => {
    const mapping = loggedInUser?.grade_mapping;
    if (mapping && typeof mapping === "object") {
      return { ...DEFAULT_GRADE_MAPPING, ...mapping };
    }
    return DEFAULT_GRADE_MAPPING;
  }, [loggedInUser]);

  const getGradeLabel = useCallback(
    grade => {
      const key = `grade_${grade}`;
      return gradeMapping[key] || `Grade ${grade}`;
    },
    [gradeMapping]
  );

  const gradeValue = useMemo(
    () => ({
      gradeMapping,
      getGradeLabel,
    }),
    [gradeMapping, getGradeLabel]
  );

  return (
    <RoleContext.Provider value={roleValue}>
      <GradeContext.Provider value={gradeValue}>
        {children}
      </GradeContext.Provider>
    </RoleContext.Provider>
  );
}

export function useGrade() {
  const context = useContext(GradeContext);
  if (!context) {
    return {
      gradeMapping: DEFAULT_GRADE_MAPPING,
      getGradeLabel: grade => `Grade ${grade}`,
    };
  }
  return context;
}

export function useRole() {
  const context = useContext(RoleContext);
  if (!context) {
    const fallbackOrg = {
      settings: {
        consider_meddpicc_role: false,
        relationship_assessments: false,
        rl_assessments_enabled: false,
        fan_in_cap: normalizeFanInCap(),
        fan_out_caps: normalizeFanOutCaps(),
        unknown_meddpicc_default_weight: normalizeUnknownMeddWeight(),
      },
    };
    return {
      organization: fallbackOrg,
      role: "",
      privilege: { exec_ops: false },
      user: null,
      rlAssessmentsEnabled: false,
      considerMeddpiccRole: false,
      orgSettings: getOrgSettings(fallbackOrg),
    };
  }
  return context;
}

export { GradeContext, RoleContext };
